import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { FileText, Copy } from "lucide-react";

export default function TechpacksGallery() {
  const [techpacks, setTechpacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/techpacks").then((r) => setTechpacks(r.data)).finally(() => setLoading(false));
  }, []);

  const copyTechpack = async (tp, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCopyingId(tp.id);
    try {
      const { data } = await api.post(`/techpacks/${tp.id}/copy`);
      toast.success("Techpack duplicated");
      navigate(`/techpacks/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCopyingId("");
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Production</p>
          <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="techpacks-title">Techpacks</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-xl">AI-drafted technical packs from your designs. Edit every field and export a factory-ready PDF.</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading…</div>
      ) : techpacks.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="techpacks-empty-state">
          <FileText size={32} className="mx-auto mb-4 text-zinc-300" />
          <p className="text-sm">No techpacks yet.</p>
          <p className="text-xs mt-2">Open a design or a collection and click <span className="font-medium text-zinc-600">Create techpack</span>.</p>
          <Link to="/designs" className="mt-4 inline-block bg-zinc-950 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">Browse designs</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {techpacks.map((tp) => (
            <Link key={tp.id} to={`/techpacks/${tp.id}`} data-testid={`techpack-card-${tp.id}`}
              className="group relative border border-zinc-200 bg-white rounded-lg overflow-hidden hover:border-zinc-400 transition-colors">
              <button data-testid={`techpack-copy-${tp.id}`} onClick={(e) => copyTechpack(tp, e)} disabled={copyingId === tp.id}
                title="Duplicate this techpack"
                className="absolute top-2 right-2 z-10 p-2 bg-white/90 border border-zinc-200 rounded-md text-zinc-500 hover:text-zinc-950 hover:border-zinc-950 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-70">
                <Copy size={13} />
              </button>
              {tp.cover_image ? (
                <img src={imgUrl(tp.cover_image)} alt={tp.style_name} className="w-full h-64 object-cover group-hover:scale-[1.02] transition-transform" />
              ) : (
                <div className="w-full h-64 bg-zinc-100 flex items-center justify-center text-zinc-300"><FileText size={40} /></div>
              )}
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{tp.product_line || tp.category}</p>
                <p className="text-sm font-medium mt-0.5 truncate">{tp.style_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-zinc-400">{tp.style_number}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${tp.process_status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                    {tp.process_status}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
